import { Button, Flex, Grid } from '@mantine/core'
import {
    ArrowLeft,
    ArrowRight,
    DoorOpen,
    LogIn,
    Package,
    RefreshCw,
} from 'lucide-react'

import { Form } from '@/components/atoms/Form'
import { WithViewModelProps } from '@/shared/types/components'

import ModalHeader from '../../shared/ModalHeader/ModalHeader'

import { useAccountContext } from '@/contexts/account-context'
import { useViewModel } from './Register.vm'
import Steps from './Steps/Steps'

export const RegisterTitle = () => {
    const { customer } = useAccountContext()

    const metadata = {
        title: !!customer
            ? `Bienvenue ${customer.first_name}! 👋`
            : 'Bienvenue à bord!',
        description:
            "Créez un compte et plongez dans l'univers du rétro avec nous!",
    }

    return <ModalHeader {...metadata} />
}

export const Register = () => {
    const viewModel = useViewModel()
    return <RegisterView viewModel={viewModel} />
}

const RegisterView = ({
    viewModel,
}: WithViewModelProps<typeof useViewModel>) => (
    <Grid columns={1} w="100%" className="h-full p-4">
        <Form
            autoComplete="off"
            className="w-full h-full"
            onSubmit={viewModel.onNext}
        >
            <StateDrivenView viewModel={viewModel} />
            <StateDrivenActions viewModel={viewModel} />
        </Form>
    </Grid>
)

const StateDrivenView = ({
    viewModel,
}: WithViewModelProps<typeof useViewModel>) => {
    switch (viewModel.currentState) {
        case 'general':
            return <Steps.General control={viewModel.formControl} />
        case 'personal':
            return <Steps.Personal control={viewModel.formControl} />
        case 'processing':
            return <Steps.Processing />
        case 'success':
            return <Steps.Success />
        case 'error':
            return <Steps.Error />
        case 'errorUserAlreadyExists':
            return <Steps.ErrorUserAlreadyExists />
        default:
            return null
    }
}

const StateDrivenActions = ({
    viewModel,
}: WithViewModelProps<typeof useViewModel>) => {
    switch (viewModel.currentState) {
        case 'general':
            return (
                <Grid.Col>
                    <Flex className="items-center justify-end w-full lg:gap-x-6">
                        <Button
                            onClick={viewModel.onSignIn}
                            color="gray.6"
                            variant="light"
                            leftIcon={<LogIn className="w-4 h-4" />}
                        >
                            J'ai déjà un compte
                        </Button>
                        <Button
                            onClick={viewModel.onNext}
                            color="blue"
                            leftIcon={<ArrowRight className="w-4 h-4" />}
                        >
                            Suivant
                        </Button>
                    </Flex>
                </Grid.Col>
            )
        case 'personal':
            return (
                <Grid.Col>
                    <Flex className="items-center justify-end w-full gap-x-6 sm:gap-x-4 lg:gap-x-6">
                        <Button
                            onClick={viewModel.onPrevious}
                            color="gray"
                            variant="light"
                            leftIcon={<ArrowLeft className="w-4 h-4" />}
                        >
                            Précédent
                        </Button>

                        <Button
                            onClick={viewModel.onNext}
                            color="blue"
                            leftIcon={<ArrowRight className="w-4 h-4" />}
                        >
                            Suivant
                        </Button>
                    </Flex>
                </Grid.Col>
            )
        case 'success':
            return (
                <Grid.Col>
                    <Flex className="items-center justify-center w-full lg:gap-x-6">
                        <Button
                            onClick={viewModel.onStartCheckout}
                            color="orange"
                            leftIcon={<Package className="w-4 h-4" />}
                        >
                            Continuer ma commande
                        </Button>
                    </Flex>
                </Grid.Col>
            )
        case 'error':
            return (
                <Grid.Col>
                    <div className="flex items-center justify-center w-full">
                        <Button
                            type="button"
                            color="dark"
                            variant="default"
                            onClick={viewModel.onRetry}
                            leftIcon={<RefreshCw className="w-4 h-4" />}
                        >
                            Réessayer
                        </Button>
                    </div>
                </Grid.Col>
            )
        case 'errorUserAlreadyExists':
            return (
                <Grid.Col>
                    <Flex className="flex items-center justify-end w-full gap-y-6 sm:gap-y-4 lg:gap-y-6">
                        <Button
                            type="button"
                            color="dark"
                            onClick={viewModel.onSignIn}
                            leftIcon={<DoorOpen className="w-4 h-4" />}
                        >
                            Se connecter
                        </Button>
                    </Flex>
                </Grid.Col>
            )
        case 'processing':
        default:
            return null
    }
}
